import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { projectFunding as c } from "@/content/projectFunding";

const ENQUIRY_URL = "/contact?topic=funding-grants";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function ProjectFunding() {
  return (
    <>
      <Seo
        title="Preparing Projects for Funding | Bioregional Ventures"
        description="Project readiness, funding pathways, partner propositions, community governance and reporting — practical preparation for community land and enterprise funding conversations."
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">{c.eyebrow}</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight text-charcoal max-w-3xl" data-testid="pf-hero-heading">
            {c.heroHeading}
          </h1>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
            {c.heroIntro}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to={ENQUIRY_URL}
              data-testid="pf-hero-primary"
              className="bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
            >
              {c.primaryCta}
            </Link>
            <button
              type="button"
              onClick={() => scrollTo("how-we-help")}
              data-testid="pf-hero-secondary"
              className="border border-eucalyptus text-eucalyptus px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus hover:text-cream transition-colors duration-300"
            >
              {c.secondaryCta}
            </button>
          </div>
        </Reveal>
      </section>

      {/* A clear project first */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="pf-clear-project-heading">
              {c.clearProjectHeading}
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-charcoal/90">
              {c.clearProjectCopy}
            </p>
          </Reveal>
        </div>
      </section>

      {/* How we can help */}
      <section id="how-we-help" className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 scroll-mt-24">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="pf-help-heading">
            {c.helpHeading}
          </h2>
        </Reveal>
        <div className="mt-12 space-y-0 divide-y divide-charcoal/10 border-y border-charcoal/10">
          {c.helpBlocks.map((block, i) => (
            <Reveal key={block.title} delay={i * 0.04}>
              <article className="py-9 md:py-11 grid grid-cols-[3rem_1fr] gap-5" data-testid={`pf-help-block-${i + 1}`}>
                <span className="font-serif text-ochre text-2xl leading-none pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-serif text-xl md:text-2xl leading-snug text-charcoal">{block.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-charcoal/85 max-w-2xl">{block.text}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="mt-10 text-base text-charcoal/80 border-l-2 border-ochre pl-4 max-w-2xl" data-testid="pf-help-closing">
            {c.helpClosing}
          </p>
        </Reveal>
      </section>

      {/* Match the funding to the work */}
      <section className="bg-eucalyptus text-cream">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug" data-testid="pf-match-heading">
              {c.matchHeading}
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-cream/85" data-testid="pf-match-intro">
              {c.matchIntroBefore}
              <Link
                to={c.matchIntroHref}
                data-testid="pf-match-link"
                className="text-ochre underline decoration-ochre underline-offset-4 hover:text-cream transition-colors duration-300"
              >
                {c.matchIntroLink}
              </Link>
              {c.matchIntroAfter}
            </p>
            <p className="mt-6 text-base leading-relaxed text-cream/85">
              Related:{" "}
              <Link
                to="/our-work#land-access"
                data-testid="pf-land-access-link"
                className="text-sm tracking-wide text-ochre underline decoration-ochre underline-offset-4 hover:text-cream transition-colors duration-300"
              >
                {c.landAccessLinkLabel}
              </Link>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Scoped outputs */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <Reveal>
          <div className="border border-charcoal/15 bg-cream p-7 md:p-8" data-testid="pf-outputs-panel">
            <h2 className="font-serif text-2xl md:text-3xl leading-snug text-charcoal">{c.outputsHeading}</h2>
            <p className="mt-3 text-xs uppercase tracking-[0.15em] text-ochre-dark">{c.outputsLabel}</p>
            <ul className="mt-5 space-y-2.5 text-sm md:text-base leading-relaxed text-charcoal/85 list-disc pl-5">
              {c.outputs.map((o) => (
                <li key={o}>{o}</li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="pf-faq-heading">
              {c.faqHeading}
            </h2>
            <Accordion type="single" collapsible className="mt-8" data-testid="pf-faq-accordion">
              {c.faqs.map((faq, i) => (
                <AccordionItem key={faq.q} value={`faq-${i}`} className="border-charcoal/15">
                  <AccordionTrigger
                    data-testid={`pf-faq-question-${i + 1}`}
                    className="text-left font-serif text-lg md:text-xl text-charcoal hover:text-ochre-dark hover:no-underline py-5"
                  >
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-charcoal/85">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="pf-cta-heading">
            {c.ctaHeading}
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-charcoal/90">{c.ctaBody}</p>
          <Link
            to={ENQUIRY_URL}
            data-testid="pf-cta-button"
            className="mt-10 inline-block bg-eucalyptus text-cream px-8 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
          >
            {c.primaryCta}
          </Link>
        </Reveal>
      </section>
    </>
  );
}
